import React, { useEffect, useState } from "react";
import { Modal, Button, Dropdown, Spinner } from "react-bootstrap";
import axios from "axios";
import { Printer, Download, Mail } from "react-feather";
import moment from "moment";
import html2pdf from "html2pdf.js";
import numberToWords from "../../utils/numberToWords";
import Swal from "sweetalert2";
import exportService from "../../../hms/utils/exportService";
import { renderDate } from "../../utils/dateFormatter";

const companyLogo = localStorage.getItem("companylogo");
const cdnUrl = process.env.REACT_APP_CDN_URL || "";
const logoNetShine = companyLogo && companyLogo.trim() ? `${cdnUrl}/${companyLogo}` : 'https://netshine-assets.blr1.digitaloceanspaces.com/company_87/logos/company_logo_87_1739613280498';
const logoSrc = companyLogo && companyLogo.trim() ? `${cdnUrl}/${companyLogo}` : logoNetShine;

// CSS for folio rendering, aligned with registration card
const styles = `
  @page { size: A4; margin: 10mm; }
  body {
    font-family: Arial, sans-serif;
    font-size: 12px;
    color: #333;
    margin: 0;
    padding: 0;
    background-color: #fff;
  }
  .folio-container {
    width: 100%;
    margin: 0 auto;
    padding: 10px;
    border: 1px solid #ccc;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
  }
  .header {
    text-align: center;
    margin-bottom: 20px;
  }
  .header img {
    max-width: 100px;
    height: auto;
  }
  .header h1 {
    font-size: 20px;
    margin: 5px 0;
    color: #2c3e50;
  }
  .section {
    margin-bottom: 15px;
  }
  .section h2 {
    font-size: 14px;
    font-weight: bold;
    color: #2c3e50;
    border-bottom: 1px solid #ccc;
    padding-bottom: 2px;
    margin-bottom: 5px;
  }
  .section table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 10px;
  }
  .section table th, .section table td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
    font-size: 12px;
  }
  .section table th {
    background-color: #f2f2f2;
    font-weight: bold;
  }
  .folio-footer {
    margin-top: 10px;
  }
  .folio-footer p {
    margin: 2px 0;
    line-height: 16px;
  }
  .folio-footer p strong {
    display: inline-block;
    width: 150px;
    font-weight: normal;
  }
  .folio-summary {
    border: 1px solid #333;
    padding: 15px;
    background-color: #f0f8ff;
    border-radius: 4px;
  }
  .amount-in-words {
    font-size: 10px;
    font-weight: 600;
    color: #555;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 15px;
  }
  .footer {
    text-align: center;
    margin-top: 20px;
    font-size: 10px;
    color: #666;
  }
  hr {
    border: 0;
    height: 0.5px;
    background: #ccc;
    margin: 10px 0;
  }
  .top-bar {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 10px;
  }
  .top-bar .logo img {
    max-width: 180px;
    height: auto;
  }
  .top-bar .property-info {
    text-align: right;
    font-size: 11px;
    line-height: 1.5;
  }
  .title {
    text-align: center;
    font-size: 20px;
    font-weight: bold;
    color: #2c3e50;
    margin-bottom: 10px;
  }
  .guest-details {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    font-size: 12px;
    margin: 10px 0 20px;
    border: 1px solid #ccc;
    padding: 10px;
    border-radius: 6px;
    background: #f9f9f9;
  }
  .guest-details div {
    min-width: 200px;
  }
`;

// Modal styles
const modalStyles = `
  .folio-preview-modal-dialog {
    width: 100%;
    max-width: 100%;
    position: fixed;
    right: 0;
    top: 0;
    height: 100vh;
    margin: 0;
    z-index: 1060;
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
  }
  .folio-preview-modal-dialog.show {
    transform: translateX(0);
  }
  .modal-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    border: none;
    border-radius: 0;
  }
  .modal-header {
    background-color: #fff;
    border-bottom: 1px solid #dee2e6;
  }
  .loader-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.9);
  }
  @media (max-width: 768px) {
    .folio-preview-modal-dialog {
      width: 100%;
    }
    .modal-footer button {
      min-width: 120px;
      margin-bottom: 5px;
    }
  }
`;

// Append modal styles to document head
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = modalStyles;
document.head.appendChild(styleSheet);

const FolioPreview = ({
  showModal,
  onClose,
  reservationId,
  guestDetails,
  onBalanceDueUpdate,
  outputType = "html",
  handleSwitchView,
  handleEmail,
  loading: externalLoading,
}) => {
  const [folioEntries, setFolioEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [companyData, setCompanyData] = useState({
    company: "Company Name",
    address: "Address",
    city: "City",
    phone: "N/A",
    email: "N/A",
  });
  const apiUrl = process.env.REACT_APP_API_URL;
  const cdnUrl = process.env.REACT_APP_CDN_URL || "";
  const companyLogo = localStorage.getItem("companyLogo");
  const logoSrc = companyLogo && companyLogo.trim() ? `${cdnUrl}/${companyLogo}` : logoNetShine;
  const businessDate = localStorage.getItem("business_date");
  const timestamp = new Date().toISOString().replace(/[:.-]/g, "");
  const suffix = `_business_date_${businessDate}_${timestamp}`;

  // Fetch company details
  useEffect(() => {
    let isMounted = false;
    const fetchCompanyDetails = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${apiUrl}/company/company-details/001`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response) {

          setCompanyData({
            company: response.data.company || "Company Name",
            address: response.data.address || "Address",
            city: response.data.city || "City",
            phone: response.data.phone || "N/A",
            email: response.data.email || "N/A",
          });
        } else {
          console.warn("Failed to fetch company details: API success is false.");
        }
      } catch (err) {
        console.error("Error fetching company details:", err);
        if (isMounted) {
          setCompanyData({
            company: "Company Name",
            address: "Address",
            city: "City",
            phone: "N/A",
            email: "N/A",
          });
        }
      }
    };

    fetchCompanyDetails();
    return () => { isMounted = false; };
  }, [apiUrl]);

  // Fetch folio data
  useEffect(() => {
    let isMounted = true;
    if (showModal && reservationId) {
      setLoading(true);
      const fetchFolioData = async () => {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(`${apiUrl}/transactions/guest-folio`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { bookingId: reservationId },
            timeout: 10000,
          });

          if (response.data.success) {
            const folio = response.data.folio || {};
            let initialBalance = 0;
            const entries = [
              ...(folio.roomRent || []).map(item => ({
                date: item.charge_date,
                description: item.reference_type || item.charge_type || "Room Rent",
                chargeType: item.charge_type,
                amount: Number(item.amount) || 0,
                taxAmount: Number(item.tax_amount) || 0,
                totalAmount: Number(item.total_amount) || 0,
                credit: 0,
                balance: 0,
                taxBreakdown: item.tax_breakdown || [],
                chargeId: item.charge_id,
              })),
              ...(folio.additionalCharges || []).map(item => ({
                date: item.charge_date,
                description: item.reference_type || item.charge_type || "Additional Charge",
                chargeType: item.charge_type,
                amount: Number(item.amount) || 0,
                taxAmount: Number(item.tax_amount) || 0,
                totalAmount: Number(item.total_amount) || 0,
                credit: 0,
                balance: 0,
                taxBreakdown: item.tax_breakdown || [],
                chargeId: item.charge_id,
              })),
              ...(folio.payments || []).map(item => ({
                date: item.charge_date,
                description: item.reference_type || "Advance Payment",
                chargeType: "ADVANCE_PAYMENT",
                amount: 0,
                taxAmount: 0,
                totalAmount: 0,
                paymentMode: item.payment_mode,
                credit: Number(item.amount) || 0,
                balance: 0,
                taxBreakdown: [],
                chargeId: item.charge_id,
              })),
            ];

            if (entries.length === 0) {
              if (isMounted) setError("No folio data available for this reservation.");
            } else {
              entries.sort((a, b) => new Date(a.date) - new Date(b.date));
              let runningBalance = initialBalance;
              const entriesWithBalance = entries.map(entry => {
                runningBalance += (entry.totalAmount || 0) - (entry.credit || 0);
                return { ...entry, balance: runningBalance };
              });
              if (isMounted) {
                setFolioEntries(entriesWithBalance);
                if (onBalanceDueUpdate) {
                  const grandTotal = entriesWithBalance.length > 0 ? entriesWithBalance[entriesWithBalance.length - 1].balance : 0;
                  onBalanceDueUpdate(grandTotal);
                }
              }
            }
          } else {
            if (isMounted) setError("Failed to load folio data: API success is false.");
          }
        } catch (err) {
          console.error("Fetch error:", err);
          if (isMounted) setError(err.message || "Network error or timeout.");
        } finally {
          if (isMounted) setLoading(false);
        }
      };
      fetchFolioData();
    } else if (!showModal && isMounted) {
      setPdfUrl("");
      setHtmlContent("");
      setError(null);
      setFolioEntries([]);
    }
    return () => { isMounted = false; };
  }, [showModal, reservationId, apiUrl, onBalanceDueUpdate]);

  // Generate HTML content
  const generateHtmlContent = () => {
    const totalRoomRent = folioEntries
      .filter(entry => entry.chargeType === "ROOM_RENT")
      .reduce((sum, entry) => sum + (entry.amount || 0), 0);
    const totalTax = folioEntries
      .reduce((sum, entry) => sum + (entry.taxAmount || 0), 0);
    const totalAdditionalCharges = folioEntries
      .filter(entry => entry.chargeType.startsWith("ADDITIONAL_CHARGES") || entry.description === "Additional Charge")
      .reduce((sum, entry) => sum + (entry.totalAmount || 0), 0);
    const totalRoomService = folioEntries
      .filter(entry => entry.description === "Room Service")
      .reduce((sum, entry) => sum + (entry.totalAmount || 0), 0);
    const totalAdvance = folioEntries
      .filter(entry => entry.chargeType === "ADVANCE_PAYMENT")
      .reduce((sum, entry) => sum + (entry.credit || 0), 0);
    const grandTotal = folioEntries.length > 0 ? folioEntries[folioEntries.length - 1].balance : 0;

    const taxBreakdownMap = {};
    folioEntries.forEach(entry => {
      if ((entry.chargeType === "ROOM_RENT" || entry.chargeType === "ADDITIONAL_CHARGES") && entry.taxBreakdown && entry.taxBreakdown.length > 0) {
        entry.taxBreakdown.forEach(tax => {
          const taxName = tax.taxName;
          const taxAmount = Number(tax.taxAmount) || 0;
          if (taxBreakdownMap[taxName]) {
            taxBreakdownMap[taxName] += taxAmount;
          } else {
            taxBreakdownMap[taxName] = taxAmount;
          }
        });
      }
    });
    const consolidatedTaxBreakdown = Object.entries(taxBreakdownMap).map(([taxName, taxAmount]) => ({
      taxName,
      taxAmount: taxAmount.toFixed(2),
    }));

    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Folio Statement</title>
        <style>
          ${styles}
          .top-bar {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
          }
          .top-bar .logo img {
            max-width: 180px;
            height: auto;
          }
          .top-bar .property-info {
            text-align: right;
            font-size: 11px;
            line-height: 1.5;
          }
          .title {
            text-align: center;
            font-size: 20px;
            font-weight: bold;
            color: #2c3e50;
            margin-bottom: 10px;
          }
          .guest-details {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            font-size: 12px;
            margin: 10px 0 20px;
            border: 1px solid #ccc;
            padding: 10px;
            border-radius: 6px;
            background: #f9f9f9;
          }
          .guest-details div {
            min-width: 200px;
          }
        </style>
      </head>
      <body>
        <div class="folio-container">
          <!-- Top Bar -->
          <div class="top-bar">
            <div class="logo">
              <img src="${logoSrc}" style="max-width: 180px;" alt="Hotel Logo" />
            </div>
            <div class="property-info">
              <h1 style="font-size: 14px; margin: 0;">${companyData.company || "Company Name"}</h1>
              <p style="line-height: 10px; margin: 2px 0; font-size: 8px;">
                ${companyData.address || "Address"}, ${companyData.city || "City"}<br>
                Phone: ${companyData.phone || "N/A"}<br>
                Email: ${companyData.email || "N/A"}
              </p>
            </div>
          </div>

          <!-- Title -->
          <div class="title">Folio Statement</div>

          <!-- Guest Details -->
          <div class="guest-details">
            <div><strong>Guest Name:</strong> ${guestDetails?.name || 'N/A'}</div>
            <div><strong>Pax:</strong> ${(guestDetails?.guests?.adults || 0) + (guestDetails?.guests?.children || 0)}</div>
            <div><strong>Phone:</strong> ${guestDetails?.phone || 'N/A'}</div>
            <div><strong>Email:</strong> ${guestDetails?.email || 'N/A'}</div>
            <div><strong>Reservation No:</strong> ${guestDetails?.bookingNumber || 'N/A'}</div>
            <div><strong>Check-in:</strong> ${guestDetails?.checkIn ? renderDate(guestDetails.checkIn) : 'N/A'}</div>
            <div><strong>Check-out:</strong> ${guestDetails?.checkOut ? renderDate(guestDetails.checkOut) : 'N/A'}</div>
          </div>

          <!-- Folio Details -->
          <div class="section">
            <h2>Folio Details</h2>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Particular</th>
                  <th>Amount</th>
                  <th>Tax Amount</th>
                  <th>Total Amount</th>
                  <th>Credit</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                ${folioEntries.map(entry => `
                  <tr>
                    <td>${renderDate(entry.date)}</td>
                    <td>${entry.description || "N/A"}</td>
                    <td>₹${Number(entry.amount || 0).toFixed(2)}</td>
                    <td>₹${Number(entry.taxAmount || 0).toFixed(2)}</td>
                    <td>₹${Number(entry.totalAmount || 0).toFixed(2)}</td>
                    <td>${entry.credit > 0 ? `₹${entry.credit.toFixed(2)}` : "-"}</td>
                    <td>₹${entry.balance.toFixed(2)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <!-- Summary -->
          <div class="folio-summary" style="display: flex; justify-content: space-between; align-items: center; margin-top: 20px;">
            <!-- Amount in Words (Left Side) -->
            <div style="flex: 1; padding-right: 20px; display: flex; align-items: center;">
              <div>
                <h6 style="margin: 0; font-weight: 600; color: #333;">Amount in Words:</h6>
                <p style="margin: 5px 0 0; color: rgb(22, 24, 22); font-weight: 600;">${numberToWords(grandTotal)}</p>
              </div>
            </div>

            <!-- Summary Details (Right Side) -->
            <div style="flex: 1; padding-left: 20px;">
              <h6 style="margin-bottom: 30px; font-weight: 600; color: #333; text-align: right;">Charge Summary</h6>
              <div style="line-height: 1.0; text-align: right;">
                <p><strong>Room Rent Charges:</strong> ₹${totalRoomRent.toFixed(2)}</p>
                ${consolidatedTaxBreakdown.length > 0
                  ? consolidatedTaxBreakdown.map(tax => `
                    <p><strong>${tax.taxName}:</strong> ₹${tax.taxAmount}</p>
                  `).join('')
                  : '<p><strong>Tax:</strong> ₹0.00</p>'
                }
                <p><strong>Additional Charges:</strong> ₹${totalAdditionalCharges.toFixed(2)}</p>
                ${totalRoomService > 0 ? `
                  <p><strong>Room Service:</strong> ₹${totalRoomService.toFixed(2)}</p>
                ` : ''}
                <p><strong>Advance:</strong> ₹${totalAdvance.toFixed(2)}</p>
                <p><strong>Grand Total:</strong> ₹${grandTotal.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="footer">
            <p>Note: All charges are final. No refunds after check-in. Late checkout charges apply. Taxes as per regulations.</p>
            <p>This is a computer-generated folio and does not require a physical signature.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Generate PDF when switching to PDF view
  useEffect(() => {
    let isMounted = true;
    if (showModal && !loading && !error && folioEntries.length > 0) {
      if (outputType === "html") {
        setHtmlContent(generateHtmlContent());
        if (pdfUrl) {
          URL.revokeObjectURL(pdfUrl);
          setPdfUrl("");
        }
      } else if (outputType === "pdf") {
        setLoading(false);
        setHtmlContent("");
        const generatePDF = async () => {
          try {
            const folioHTML = generateHtmlContent();
            const token = localStorage.getItem("token");
            const response = await axios.post(
              `${apiUrl}/transactions/generate-pdf`,
              { html: folioHTML },
              {
                headers: { Authorization: `Bearer ${token}` },
                responseType: "arraybuffer",
              }
            );

            const pdfBlob = new Blob([response.data], { type: "application/pdf" });
            if (isMounted) {
              const url = URL.createObjectURL(pdfBlob);
              setPdfUrl(url);
              setLoading(false);
            }
          } catch (err) {
            console.error("Error generating folio PDF:", err);
            if (isMounted) {
              setError(err.message || "Failed to generate PDF from server.");
              setLoading(false);
              Swal.fire({
                title: "Error",
                text: err.message || "Failed to generate PDF from server.",
                icon: "error",
              });
            }
          }
        };
        generatePDF();
      }
    } else if (showModal && !loading && error) {
      setLoading(false);
    }
    return () => { isMounted = false; };
  }, [showModal, folioEntries, outputType, loading, error, reservationId, guestDetails, apiUrl]);

  // Cleanup PDF URL
  useEffect(() => {
    let isMounted = true;
    if (!showModal && pdfUrl && isMounted) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl("");
    }
    return () => { isMounted = false; };
  }, [showModal, pdfUrl]);

  // Disable background scroll
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
      document.body.style.height = "100vh";
    } else {
      document.body.style.overflow = "auto";
      document.body.style.height = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
      document.body.style.height = "auto";
    };
  }, [showModal]);

  const handleClose = () => {
    onClose();
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl("");
    }
  };

  const handleDownloadPDF = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `folio_${reservationId}_${suffix}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrint = () => {
    if (htmlContent) {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>Print Folio</title>
            <style>${styles}</style>
          </head>
          <body>${htmlContent}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  const handleViewSwitch = (newOutputType) => {
    handleSwitchView(newOutputType);
  };

  const handleExportXLS = () => {
    const exportData = folioEntries.map(entry => ({
      date: moment(entry.date).format("DD-MM-YYYY"),
      description: entry.description,
      amount: `₹${Number(entry.amount || 0).toFixed(2)}`,
      taxAmount: `₹${Number(entry.taxAmount || 0).toFixed(2)}`,
      totalAmount: `₹${Number(entry.totalAmount || 0).toFixed(2)}`,
      credit: entry.credit > 0 ? `₹${entry.credit.toFixed(2)}` : "-",
      balance: `₹${entry.balance.toFixed(2)}`,
    }));
    exportService.exportToXLS(
      exportData,
      [
        { key: "date", label: "Date" },
        { key: "description", label: "Particular" },
        { key: "amount", label: "Amount" },
        { key: "taxAmount", label: "Tax Amount" },
        { key: "totalAmount", label: "Total Amount" },
        { key: "credit", label: "Credit" },
        { key: "balance", label: "Balance" },
      ],
      `folio_${reservationId}_${suffix}.xlsx`,
      `Guest Folio - ${guestDetails?.name || "Guest"}`
    );
  };

  const handleExportCSV = () => {
    const exportData = folioEntries.map(entry => ({
      date: moment(entry.date).format("DD-MM-YYYY"),
      description: entry.description,
      amount: `₹${Number(entry.amount || 0).toFixed(2)}`,
      taxAmount: `₹${Number(entry.taxAmount || 0).toFixed(2)}`,
      totalAmount: `₹${Number(entry.totalAmount || 0).toFixed(2)}`,
      credit: entry.credit > 0 ? `₹${entry.credit.toFixed(2)}` : "-",
      balance: `₹${entry.balance.toFixed(2)}`,
    }));
    exportService.exportToCSV(
      exportData,
      [
        { key: "date", label: "Date" },
        { key: "description", label: "Particular" },
        { key: "amount", label: "Amount" },
        { key: "taxAmount", label: "Tax Amount" },
        { key: "totalAmount", label: "Total Amount" },
        { key: "credit", label: "Credit" },
        { key: "balance", label: "Balance" },
      ],
      `folio_${reservationId}_${suffix}.csv`
    );
  };

  const handleExportPDF = async () => {
    try {
      const element = document.createElement("div");
      element.innerHTML = generateHtmlContent();
      const options = {
        margin: 10,
        filename: `folio_${reservationId}_${suffix}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };
      const pdfBlob = await html2pdf().set(options).from(element).output("blob");
      const link = document.createElement("a");
      link.href = URL.createObjectURL(pdfBlob);
      link.download = `folio_${reservationId}_${suffix}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error exporting folio PDF:", err);
      Swal.fire({
        title: "Error",
        text: "Failed to export folio as PDF.",
        icon: "error",
      });
    }
  };

  const handleFolioEmail = async () => {
    if (!guestDetails?.email || guestDetails.email.trim().toUpperCase() === "N/A") {
      Swal.fire({
        title: "Email Missing",
        text: "No email address found for this guest. Please add an email to proceed.",
        icon: "warning",
      });
      return;
    }
    Swal.fire({
      title: "Send Folio Email",
      text: `Are you sure you want to send the folio to ${guestDetails.email}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Send",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const folioHTML = generateHtmlContent();
          const token = localStorage.getItem("token");
          const response = await axios.post(`${apiUrl}/transactions/send-folio-email`, [
            {
              bookingId: reservationId,
              guestName: guestDetails?.name || "Guest",
              guestEmail: guestDetails.email,
              checkInDate: guestDetails?.checkIn ? moment(guestDetails.checkIn).format("YYYY-MM-DD") : "N/A",
              checkOutDate: guestDetails?.checkOut ? moment(guestDetails.checkOut).format("YYYY-MM-DD") : "N/A",
              invoiceNo: guestDetails?.bookingNumber || "N/A",
              amount: folioEntries.length > 0 ? folioEntries[folioEntries.length - 1].balance : 0,
              html: folioHTML,
            },
          ], {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (response.data.success) {
            Swal.fire({
              title: "Email Sent",
              text: `Folio successfully sent to ${guestDetails.email}!`,
              icon: "success",
            });
          } else {
            throw new Error(response.data.message || "Failed to send email.");
          }
        } catch (err) {
          console.error("Error sending folio email:", err);
          Swal.fire({
            title: "Error",
            text: err.message || "Failed to send folio email.",
            icon: "error",
          });
        }
      }
    });
  };

  const downloadMenu = (
    <Dropdown.Menu>
      <Dropdown.Item onClick={handleExportXLS}>Export XLS</Dropdown.Item>
      <Dropdown.Item onClick={handleExportCSV}>Export CSV</Dropdown.Item>
      <Dropdown.Item onClick={handleExportPDF}>Export PDF</Dropdown.Item>
      <Dropdown.Item onClick={handlePrint}>Print</Dropdown.Item>
    </Dropdown.Menu>
  );

  const title = "Folio Statement Preview";

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      keyboard={false}
      dialogClassName="folio-preview-modal-dialog"
      backdropClassName="modal-backdrop"
      style={{ zIndex: 1060 }}
    >
      <Modal.Header
        style={{
          flexShrink: 0,
          padding: "10px 20px",
          borderBottom: "1px solid #dee2e6",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#fff",
        }}
      >
        <Modal.Title id="folioPreviewModalLabel">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{
          flex: 1,
          padding: "5mm",
          overflowY: "auto",
          height: "calc(100vh - 140px)",
          position: "relative",
        }}
      >
        {(loading || externalLoading) ? (
          <div className="loader-container">
            <Spinner animation="border" role="status" style={{ width: "3rem", height: "3rem" }} />
            <p style={{ marginTop: "10px", fontSize: "16px" }}>
              {externalLoading ? "Preparing..." : "Loading Folio..."}
            </p>
          </div>
        ) : htmlContent ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              minHeight: "100%",
            }}
          >
            <div
              id="folio-content"
              style={{
                width: "100%",
                maxWidth: "95%",
                height: "auto",
                padding: "10px",
                boxSizing: "border-box",
                backgroundColor: "#fff",
                boxShadow: "0 0 5px rgba(0,0,0,0.1)",
                overflow: "auto",
              }}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </div>
        ) : pdfUrl ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              height: "100%",
              overflowY: "auto",
            }}
          >
            <iframe
              src={pdfUrl}
              style={{
                width: "100%",
                maxWidth: "100%",
                height: "100%",
                border: "none",
              }}
              title={title}
              loading="lazy"
              scrolling="auto"
            />
          </div>
        ) : error ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "red",
            }}
          >
            {error}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
          </div>
        )}
      </Modal.Body>
      <Modal.Footer
        style={{
          flexShrink: 0,
          padding: "10px 20px",
          borderTop: "1px solid #dee2e6",
          backgroundColor: "#fff",
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <Button
          variant={outputType === "pdf" ? "outline-primary" : "primary"}
          onClick={() => handleViewSwitch(outputType === "pdf" ? "html" : "pdf")}
          disabled={loading || externalLoading || !folioEntries.length}
          style={{
            fontSize: "14px",
            padding: "6px 16px",
            minWidth: "140px",
          }}
        >
          Switch to {outputType === "pdf" ? "HTML" : "PDF"} View
        </Button>
        {outputType === "pdf" ? (
          <Button
            variant="primary"
            onClick={handleDownloadPDF}
            disabled={!pdfUrl || loading || externalLoading}
            style={{
              fontSize: "14px",
              padding: "6px 16px",
              minWidth: "140px",
            }}
          >
            <Download size={16} className="me-2" /> Download PDF
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handlePrint}
            disabled={!htmlContent || loading || externalLoading}
            style={{
              fontSize: "14px",
              padding: "6px 16px",
              minWidth: "140px",
            }}
          >
            <Printer size={16} className="me-2" /> Print
          </Button>
        )}
        <Dropdown>
          <Dropdown.Toggle
            variant="secondary"
            id="dropdown-download"
            style={{
              fontSize: "14px",
              padding: "6px 16px",
              minWidth: "140px",
            }}
          >
            <Download size={16} className="me-2" /> Download
          </Dropdown.Toggle>
          {downloadMenu}
        </Dropdown>
        <Button
          variant="secondary"
          onClick={handleFolioEmail}
          disabled={loading || externalLoading || !folioEntries.length}
          style={{
            fontSize: "14px",
            padding: "6px 16px",
            minWidth: "140px",
          }}
        >
          <Mail size={16} className="me-2" /> Email
        </Button>
        <Button
          variant="secondary"
          onClick={handleClose}
          style={{
            fontSize: "14px",
            padding: "6px 16px",
            minWidth: "140px",
          }}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FolioPreview;